import React, { useEffect } from 'react';
import Header from './Components/Header';
import MainHeader from './Components/MainHeader';
import 'aos/dist/aos.css';
import AOS from 'aos';
import Footer from './Components/Footer';
import ServicesCTABanner from './Components/ServicesCTABanner';
import { NavLink } from 'react-router-dom';
import { servicesData } from './Components/servicesData';
import { FaArrowRight, FaShieldAlt, FaRocket, FaHandshake, FaLock } from 'react-icons/fa';
import SEOHelmet from './Components/SEOHelmet';
import './servicesPage.css';

const Services = () => {
    useEffect(() => {
        AOS.init({
            offset: 150,
            duration: 1000,
        });
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.octalbees.com/"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": "https://www.octalbees.com/services"
            }
        ]
    };

    return (
        <div className="ServicesPage">
            <SEOHelmet
                title="Our Services – Web, App, AI & Digital Solutions"
                description="Explore Octalbees' full range of technology services including web development, mobile apps, AI/ML, data engineering, fintech, digital marketing, and UI/UX design."
                path="/services"
                breadcrumbSchema={breadcrumbSchema}
            />
            <Header />
            <MainHeader />

            {/* Hero Section - Redesigned */}
            <section className="solutions-hero">
                {/* Dynamic Background Elements */}
                <div className="tech-pattern-overlay"></div>
                <div className="hero-glow"></div>
                <div className="hero-glow hero-glow-2"></div>

                <div className="container item-relative">
                    <div className="row justify-content-center text-center">
                        <div className="col-lg-10">
                            {/* Floating Badge */}
                            <div className="hero-badge-wrapper">
                                <div className="hero-badge">
                                    <span className="pulse-dot"></span>
                                    INNOVATION FIRST
                                </div>
                            </div>

                            <h1 className="solutions-title">
                                Technology Solutions <br />
                                <span className="highlight-animated">That Scale Your Business</span>
                            </h1>

                            <p className="solutions-subtitle mx-auto text-center">
                                From web and mobile development to AI and digital marketing — we deliver full-spectrum digital ecosystems designed to accelerate growth and outperform competitors.
                            </p>

                            <div className="hero-cta-group">
                                <NavLink to="/contact-us" className="btn-premium-primary">
                                    Start a Project <FaArrowRight className="ms-2" />
                                </NavLink>
                                <a href="#solutions" className="btn-premium-secondary" onClick={(e) => {
                                    e.preventDefault();
                                    document.querySelector('.solutions-grid-section').scrollIntoView({ behavior: 'smooth' });
                                }}>
                                    Explore Solutions
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Partner With Us Section */}
            <section className="spt-trust-section">
                <div className="container">
                    <div className="text-center mb-5">
                        <span className="spt-trust-overline">The Octalbees Advantage</span>
                        <h2 className="spt-trust-title">Why Companies Partner With Us</h2>
                    </div>
                    <div className="row g-4">
                        <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="100">
                            <div className="spt-trust-pillar">
                                <FaShieldAlt className="spt-trust-icon" />
                                <h4>100% IP Ownership</h4>
                                <p>You own every line of code, design asset, and database schema from day one. No vendor lock-in, ever.</p>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="200">
                            <div className="spt-trust-pillar">
                                <FaRocket className="spt-trust-icon" />
                                <h4>30-Day Free Support</h4>
                                <p>We stand by our work. Every project includes a full month of post-launch technical support and bug fixing.</p>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="300">
                            <div className="spt-trust-pillar">
                                <FaLock className="spt-trust-icon" />
                                <h4>NDA-First Approach</h4>
                                <p>Your ideas are safe. We sign strict Non-Disclosure Agreements before discussing any project details.</p>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="400">
                            <div className="spt-trust-pillar">
                                <FaHandshake className="spt-trust-icon" />
                                <h4>Dedicated Team</h4>
                                <p>No outsourcing to unverified freelancers. Your project is handled entirely by our in-house engineering team.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* All Solutions Grid */}
            <section className="solutions-grid-section" id="solutions">
                <div className="container">
                    <div className="spt-grid-header" data-aos="fade-up">
                        <span className="spt-grid-overline">FULL-SPECTRUM CAPABILITIES</span>
                        <h2 className="spt-grid-title">Every Solution Your Business Needs</h2>
                        <p className="spt-grid-subtitle">From concept to deployment, we cover the entire technology lifecycle under one roof.</p>
                    </div>
                    <div className="row g-4">
                        {['custom-software', 'erp-crm', 'website-development', 'app-development', 'business-automation', 'ui-ux-design', 'digital-marketing', 'data-engineering', 'ai-ml', 'robotics', 'app-compliance'].map((key, index) => {
                            const service = servicesData[key];
                            return (
                                <div className="col-lg-4 col-md-6" key={key} data-aos="fade-up" data-aos-delay={(index % 3) * 100}>
                                    <div className="solution-card-modern">
                                        <div className="card-icon-container">
                                            {service.subServices[0].icon} {/* Taking the first subservice icon as representative, or add specific main icon to data */}
                                        </div>
                                        <h3 className="card-heading">{service.heroTitle}</h3>
                                        <p className="card-intro text-truncate-4">{service.introText}</p>

                                        <div className="tech-tags">
                                            {service.subServices.slice(0, 3).map((sub, i) => (
                                                <span key={i} className="tech-tag">{sub.title}</span>
                                            ))}
                                        </div>

                                        <NavLink to={`/services/${key}`} className="learn-more-btn" onClick={scrollToTop}>
                                            Explore Solution <FaArrowRight className="ms-2" />
                                        </NavLink>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <ServicesCTABanner />
            <Footer />


        </div>
    );
};

export default Services;
